/**
 * Fiş yazıcısı ayarları — her bilgisayarın tarayıcısında saklanır (yazıcı o kasaya özgüdür).
 * Fişi panel değil, kasadaki bilgisayarda çalışan Mutfacık Yazıcı programı (yazici-kurulum/KURULUM.bat)
 * yazıcıya iletir; ayarlar her istekle o sunucuya gönderilir.
 */

export type PrinterCharset = "tr" | "ascii" | "image";

/**
 * "network": ağdaki yazıcı (IP:9100), kasa bilgisayarındaki program üzerinden
 * "windows": bu bilgisayara USB ile bağlı, Windows'a eklenmiş yazıcı
 * "rawbt":   Android tablet — fiş tarayıcıda hazırlanır, RawBT uygulaması yazıcıya basar (bilgisayar gerekmez)
 */
export type PrinterConnection = "network" | "windows" | "rawbt";

export interface PrinterSettings {
  /** Yerel yazdırma sunucusunun adresi */
  serverUrl: string;
  connection: PrinterConnection;
  /** USB / Windows yazıcısının Windows'taki adı */
  printerName: string;
  /** Yazıcının ağdaki IP adresi (ör. 192.168.1.50) */
  host: string;
  /** Ağ yazıcılarında genelde 9100 */
  port: number;
  /** Satır başına karakter: 80 mm kağıt ≈ 48, 58 mm ≈ 32 */
  width: 48 | 32;
  /**
   * "tr": Türkçe karakter (CP857), "ascii": ç→c gibi sadeleştir,
   * "image": fiş resim olarak basılır — Türkçe tablosu olmayan yazıcılar için (yalnız yazdırma programıyla)
   */
  charset: PrinterCharset;
  /** ESC/POS kod sayfası numarası — Epson uyumlularda PC857 (Türkçe) = 13 */
  codePage: number;
  /** Her fişten kaç kopya */
  copies: number;
  /** Fiş sonunda kağıdı kes */
  cut: boolean;
}

const KEY = "printerSettings";

export const DEFAULT_PRINTER_SETTINGS: PrinterSettings = {
  serverUrl: "http://127.0.0.1:4000",
  connection: "network",
  printerName: "",
  host: "",
  port: 9100,
  width: 48,
  charset: "tr",
  codePage: 13,
  copies: 1,
  cut: true,
};

export function loadPrinterSettings(): PrinterSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_PRINTER_SETTINGS };
    return { ...DEFAULT_PRINTER_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_PRINTER_SETTINGS };
  }
}

export function savePrinterSettings(s: PrinterSettings) {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* özel pencere vb. — oturumluk kalır */ }
}

export function isPrinterConfigured(s: PrinterSettings = loadPrinterSettings()): boolean {
  if (s.connection === "rawbt") return true; // yazıcı RawBT uygulamasının içinde seçilir
  return s.connection === "windows" ? !!s.printerName.trim() : !!s.host.trim();
}

/** Sunucuya giden yazıcı bloğu */
export function printerPayload(s: PrinterSettings) {
  return {
    type: s.connection, printerName: s.printerName.trim(),
    host: s.host.trim(), port: Number(s.port) || 9100, width: s.width, charset: s.charset,
    codePage: Number(s.codePage), copies: Number(s.copies) || 1, cut: s.cut,
  };
}

export interface PrintResult { ok: boolean; message: string }

export interface DiscoveredPrinter { host: string; port: number; model: string }
export interface WindowsPrinter { name: string; port: string; driver: string; status: string; usb: boolean }
export interface DiscoverResult {
  ok: boolean;
  message: string;
  /** Ağda 9100 portu açık cihazlar */
  printers: DiscoveredPrinter[];
  subnets: string[];
  /** Windows'a eklenmiş yazıcılar */
  windowsPrinters: WindowsPrinter[];
  /** USB'de tanınan ama Windows'a yazıcı olarak eklenmemiş cihazlar */
  unconfiguredUsb: { name: string }[];
}

/** Yerel sunucuya ağı taratır (9100 portu açık cihazlar). Birkaç saniye sürer. */
export async function discoverPrinters(settings: PrinterSettings, port = 9100): Promise<DiscoverResult> {
  const base = settings.serverUrl.trim().replace(/\/+$/, "") || DEFAULT_PRINTER_SETTINGS.serverUrl;
  try {
    const res = await fetch(`${base}/api/printers/discover?port=${port}`);
    const data = await res.json();
    const empty = { printers: [], subnets: [], windowsPrinters: [], unconfiguredUsb: [] };
    if (!res.ok || !data.success) {
      return { ok: false, message: data.error ?? (res.status === 404 ? "Yazdırma programı eski sürüm; KURULUM.bat ile güncelleyin." : "Tarama başarısız."), ...empty };
    }
    return {
      ok: true, message: "", printers: data.printers ?? [], subnets: data.subnets ?? [],
      windowsPrinters: data.windowsPrinters ?? [], unconfiguredUsb: data.unconfiguredUsb ?? [],
    };
  } catch {
    return {
      ok: false, message: `Yazdırma sunucusuna ulaşılamadı (${base}). Bu bilgisayarda Mutfacık Yazıcı kurulu mu? (yazici-kurulum/KURULUM.bat)`,
      printers: [], subnets: [], windowsPrinters: [], unconfiguredUsb: [],
    };
  }
}

/** Yerel sunucuya istek atar; ağ hatasını "sunucu çalışmıyor" olarak anlamlandırır. */
export async function callPrintServer(path: string, body: unknown, settings: PrinterSettings, method: "GET" | "POST" = "POST"): Promise<PrintResult> {
  const base = settings.serverUrl.trim().replace(/\/+$/, "") || DEFAULT_PRINTER_SETTINGS.serverUrl;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 12000);
  try {
    const res = await fetch(`${base}${path}`, {
      method,
      headers: method === "POST" ? { "Content-Type": "application/json" } : undefined,
      body: method === "POST" ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
    let data: { success?: boolean; error?: string; message?: string } = {};
    try { data = await res.json(); } catch { /* eski sunucu HTML hata döndürebilir */ }
    if (res.ok && data.success !== false) return { ok: true, message: data.message ?? "Tamam." };
    return {
      ok: false,
      message: data.error ?? (res.status === 404
        ? "Yazdırma sunucusu eski sürüm. KURULUM.bat ile güncelleyin."
        : `Yazdırma sunucusu hata döndü (${res.status}).`),
    };
  } catch {
    return {
      ok: false,
      message: `Yazdırma sunucusuna ulaşılamadı (${base}). Bu bilgisayarda Mutfacık Yazıcı kurulu mu? (yazici-kurulum/KURULUM.bat)`,
    };
  } finally {
    clearTimeout(timer);
  }
}
